import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Button,
  TextField,
  Grid,
  Typography,
  Divider,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import dayjs from "dayjs";
import {
  getShipmentDetail,
  getShipmentPurchaseOrdersByShipmentId,
  patchShipment,
  updateBatchShipmentPurchaseOrders,
} from "../../api/imports";
import { getProviderList } from "../../api/providers";
import {
  getCatalogue,
  getStatusCatalogue,
} from "../../api/common/catalogues.ts";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import DropdownSelector from "../../atoms/Common/DropdownSelector.tsx";
import ShipmentPurchaseOrderFields from "./ShipmentPurchaseOrderFields";

// Fields managed by the Incrementables form are intentionally excluded here:
// oceanFreightCost, customsAgentCost, forwarderLandCost, storageCost, delayCharges.

const validationSchema = Yup.object().shape({
  ref: Yup.string().required("Referencia es obligatoria"),
  portArrivalDate: Yup.date()
    .typeError("Fecha inválida")
    .required("Fecha de llegada a puerto es obligatoria"),
});

const EMPTY_DATE = "0001-01-01";

const initialValues = {
  statusId: "",
  providerId: "",
  ref: "",
  isFsc: false,
  tariffFractionId: "",
  igi: "",
  totalNetWeight: "",
  cbm: "",
  requestDate: null,
  deliveryDate: null,
  departureDate: null,
  shipmentTransportTypeId: "",
  portDestinationId: "",
  foreignTransportId: "",
  customsAgentId: "",
  masterBillofLanding: "",
  hasForwarder: false,
  forwarderId: "",
  forwarderGuideNo: "",
  portOfOriginId: "",
  patent: "",
  requestNo: "",
  invoiceNo: "",
  shipmentTypeId: "",
  shipmentModeId: "",
  forwarderLandId: "",
  freeDays: "",
  portArrivalDate: null,
  plantArrivalDate: null,
  hasStorage: false,
  observations: "",
};

const parseDate = (value) => {
  if (!value || value === EMPTY_DATE) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const ShipmentEditForm = ({ onSubmit, id, companyId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(initialValues);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const { openSnack } = useSnack();

  const fetchPurchaseOrders = async () => {
    try {
      const assignedResponse = await getShipmentPurchaseOrdersByShipmentId(id);
      setPurchaseOrders(assignedResponse.data || []);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getShipmentDetail(id);
      const data = res.data;
      await fetchPurchaseOrders();
      setFormData({
        statusId: data.statusId ?? "",
        providerId: data.providerId ?? "",
        ref: data.ref ?? "",
        isFsc: data.isFsc ?? false,
        tariffFractionId: data.tariffFractionId ?? "",
        igi: data.igi ?? "",
        totalNetWeight: data.totalNetWeight ?? "",
        cbm: data.cbm ?? "",
        requestDate: parseDate(data.requestDate),
        deliveryDate: parseDate(data.deliveryDate),
        departureDate: parseDate(data.departureDate),
        shipmentTransportTypeId: data.shipmentTransportTypeId ?? "",
        portDestinationId: data.portDestinationId ?? "",
        foreignTransportId: data.foreignTransportId ?? "",
        customsAgentId: data.customsAgentId ?? "",
        masterBillofLanding: data.masterBillofLanding ?? "",
        hasForwarder: data.hasForwarder ?? false,
        forwarderId: data.forwarderId ?? "",
        forwarderGuideNo: data.forwarderGuideNo ?? "",
        portOfOriginId: data.portOfOriginId ?? "",
        patent: data.patent ?? "",
        requestNo: data.requestNo ?? "",
        invoiceNo: data.invoiceNo ?? "",
        shipmentTypeId: data.shipmentTypeId ?? "",
        shipmentModeId: data.shipmentModeId ?? "",
        forwarderLandId: data.forwarderLandId ?? "",
        freeDays: data.freeDays ?? "",
        portArrivalDate: parseDate(data.portArrivalDate),
        plantArrivalDate: parseDate(data.plantArrivalDate),
        hasStorage: data.hasStorage ?? false,
        observations: data.observations ?? "",
      });
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const toInt = (value) =>
    value === "" || value === null || value === undefined
      ? null
      : parseInt(value, 10);

  const toNumber = (value) =>
    value === "" || value === null || value === undefined
      ? null
      : Number(value);

  const toDateString = (value) =>
    value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : null;

  const handleAddPurchaseOrder = (purchaseOrder) => {
    setPurchaseOrders((prev) => [
      ...prev,
      {
        ...purchaseOrder,
        shipmentId: id,
      },
    ]);
  };

  const handleRemovePurchaseOrder = (purchaseOrderId) => {
    setPurchaseOrders((prev) =>
      prev.filter((po) => po.purchaseOrderId !== purchaseOrderId),
    );
  };

  const handleSubmit = async (values) => {
    const payload = {
      statusId: toInt(values.statusId),
      providerId: values.providerId || null,
      ref: values.ref,
      isFsc: values.isFsc,
      tariffFractionId: toInt(values.tariffFractionId),
      igi: toNumber(values.igi),
      totalNetWeight: toNumber(values.totalNetWeight),
      cbm: toNumber(values.cbm),
      requestDate: toDateString(values.requestDate),
      deliveryDate: toDateString(values.deliveryDate),
      departureDate: toDateString(values.departureDate),
      shipmentTransportTypeId: toInt(values.shipmentTransportTypeId),
      portDestinationId: toInt(values.portDestinationId),
      foreignTransportId: toInt(values.foreignTransportId),
      customsAgentId: toInt(values.customsAgentId),
      masterBillofLanding: values.masterBillofLanding,
      hasForwarder: values.hasForwarder,
      forwarderId: toInt(values.forwarderId),
      forwarderGuideNo: values.forwarderGuideNo,
      portOfOriginId: toInt(values.portOfOriginId),
      patent: values.patent,
      requestNo: values.requestNo,
      invoiceNo: values.invoiceNo,
      shipmentTypeId: toInt(values.shipmentTypeId),
      shipmentModeId: toInt(values.shipmentModeId),
      forwarderLandId: toInt(values.forwarderLandId),
      freeDays: toInt(values.freeDays),
      portArrivalDate: toDateString(values.portArrivalDate),
      plantArrivalDate: toDateString(values.plantArrivalDate),
      hasStorage: values.hasStorage,
      observations: values.observations,
    };

    try {
      setSaving(true);
      await patchShipment(id, payload);
      await updateBatchShipmentPurchaseOrders(
        purchaseOrders.map((po) => ({
          id: po.id,
          purchaseOrderId: po.purchaseOrderId,
          shipmentId: parseInt(id, 10),
          companyId,
        })),
      );
      openSnack("Embarque actualizado exitosamente", "success");
      onSubmit?.();
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, handleBlur, setFieldValue, errors, touched }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">Datos Generales</Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="providerId"
                label="Proveedor"
                fetchFunction={getProviderList}
                fetchParams={{ companyId, pageSize: 1000 }}
                optionLabel="code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="ref"
                name="ref"
                label="Referencia"
                value={values.ref}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.ref && Boolean(errors.ref)}
                helperText={touched.ref && errors.ref}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="statusId"
                label="Estatus"
                fetchFunction={getStatusCatalogue}
                fetchParams="SHIPMENT"
                optionLabel="name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="invoiceNo"
                name="invoiceNo"
                label="No. Factura"
                value={values.invoiceNo}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isFsc"
                    checked={values.isFsc}
                    onChange={handleChange}
                  />
                }
                label="FSC"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5">Material y Aduana</Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="tariffFractionId"
                label="Fracción Arancelaria"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "TARIFF_FRACTION", pageSize: 1000 }}
                optionLabel={["code", "name"]}
                optionSeparator=" | "
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="igi"
                name="igi"
                label="IGI (%)"
                type="number"
                value={values.igi}
                onChange={handleChange}
                onBlur={handleBlur}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="totalNetWeight"
                name="totalNetWeight"
                label="Peso Neto del material (kg)"
                type="number"
                value={values.totalNetWeight}
                onChange={handleChange}
                onBlur={handleBlur}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="cbm"
                name="cbm"
                label="CBM"
                type="number"
                value={values.cbm}
                onChange={handleChange}
                onBlur={handleBlur}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="customsAgentId"
                label="Agente Aduanal"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "CUSTOMS_AGENT", pageSize:1000 }}
                optionLabel="code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="patent"
                name="patent"
                label="Patente"
                value={values.patent}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="requestNo"
                name="requestNo"
                label="No. Pedimento"
                value={values.requestNo}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5">Transporte</Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="shipmentTransportTypeId"
                label="Tipo de Envío"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "SHIPMENT_TRANSPORT_TYPE", pageSize:1000 }}
                optionLabel="code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="shipmentTypeId"
                label="Tipo de Embarque"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "SHIPMENT_TYPE", pageSize:1000 }}
                optionLabel="name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="shipmentModeId"
                label="Modo de Embarque"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "SHIPMENT_MODE", pageSize:1000 }}
                optionLabel="name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="foreignTransportId"
                label="Transporte Extranjero"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "FOREIGN_TRANSPORT", pageSize:1000 }}
                optionLabel="code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="portOfOriginId"
                label="Puerto de Origen"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "PORT_OF_ORIGIN", pageSize:1000 }}
                optionLabel="code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="portDestinationId"
                label="Puerto de Llegada"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "PORT_DESTINATION", pageSize:1000 }}
                optionLabel="code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="masterBillofLanding"
                name="masterBillofLanding"
                label="Master Bill of Landing"
                value={values.masterBillofLanding}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="freeDays"
                name="freeDays"
                label="Días Libres"
                type="number"
                value={values.freeDays}
                onChange={handleChange}
                onBlur={handleBlur}
                InputProps={{ inputProps: { min: 0, step: 1 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5">Forwarder</Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="hasForwarder"
                    checked={values.hasForwarder}
                    onChange={handleChange}
                  />
                }
                label="Tiene Forwarder"
              />
            </Grid>
            <Grid item xs={12} sm={6} />
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="forwarderId"
                label="Forwarder"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "FORWARDER", pageSize:1000 }}
                optionLabel="code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="forwarderGuideNo"
                name="forwarderGuideNo"
                label="No. Guía Forwarder"
                value={values.forwarderGuideNo}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelector
                name="forwarderLandId"
                label="Forwarder Terrestre"
                fetchFunction={getCatalogue}
                fetchParams={{ CatalogueName: "FORWARDER_LAND", pageSize:1000 }}
                optionLabel="code"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5">Fechas</Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MyDatePicker
                label="Fecha de Pedido"
                value={values.requestDate ? dayjs(values.requestDate) : null}
                onChange={(value) => setFieldValue("requestDate", value)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MyDatePicker
                label="Fecha de Entrega"
                value={values.deliveryDate ? dayjs(values.deliveryDate) : null}
                onChange={(value) => setFieldValue("deliveryDate", value)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MyDatePicker
                label="Fecha de Zarpe"
                value={
                  values.departureDate ? dayjs(values.departureDate) : null
                }
                onChange={(value) => setFieldValue("departureDate", value)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MyDatePicker
                label="Fecha de Llegada al Puerto"
                value={
                  values.portArrivalDate ? dayjs(values.portArrivalDate) : null
                }
                onChange={(value) => setFieldValue("portArrivalDate", value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: touched.portArrivalDate && Boolean(errors.portArrivalDate),
                    helperText: touched.portArrivalDate && errors.portArrivalDate,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MyDatePicker
                label="Fecha de Llegada a Planta"
                value={
                  values.plantArrivalDate ? dayjs(values.plantArrivalDate) : null
                }
                onChange={(value) => setFieldValue("plantArrivalDate", value)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5">Otros</Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="hasStorage"
                    checked={values.hasStorage}
                    onChange={handleChange}
                  />
                }
                label="Tiene Almacenaje"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                id="observations"
                name="observations"
                label="Observaciones"
                value={values.observations}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5">Órdenes de Compra</Typography>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <ShipmentPurchaseOrderFields
                companyId={companyId}
                purchaseOrders={purchaseOrders}
                onAdd={handleAddPurchaseOrder}
                onRemove={handleRemovePurchaseOrder}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={saving}
              >
                {saving ? "Guardando..." : "Aceptar"}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ShipmentEditForm;
